import { useCallback, useEffect, useRef } from "react";
import type { Resolver, FieldValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodType } from "zod";
import { useAddressFields as useAddressFieldsBase } from "@fluid-app/address-fields/hooks/use-address-fields";
import type { AddressField } from "@fluid-app/address-fields/types";
import { useTranslation } from "@/hooks/useTranslation";

export { isFieldRequired } from "@fluid-app/address-fields/country-config";

export type { AddressField } from "@fluid-app/address-fields/types";

export function useAddressFields(countryCode: string) {
  const { language } = useTranslation();
  return useAddressFieldsBase(countryCode, language);
}

export function useDynamicAddressResolver<T extends FieldValues>(
  addressFields: AddressField[],
  buildSchema: (fields: AddressField[]) => ZodType,
): Resolver<T> {
  const addressFieldsRef = useRef<AddressField[]>([]);
  const buildSchemaRef = useRef(buildSchema);

  useEffect(() => {
    addressFieldsRef.current = addressFields;
  }, [addressFields]);

  useEffect(() => {
    buildSchemaRef.current = buildSchema;
  });

  return useCallback<Resolver<T>>((values, context, options) => {
    const schema = buildSchemaRef.current(addressFieldsRef.current);
    return zodResolver(schema as any)(values, context, options);
  }, []);
}
